"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { ChevronLeft } from "lucide-react"
import { useUserRole } from "@/providers/user-role-provider"
import { useNavTheme } from "@/hooks/use-nav-theme"
import { readClientView } from "@/lib/client-view"
import { stripLocalePrefix, isGlassNavPath, isChromelessPath } from "@/i18n/routing"
import { isRootPath, parentPath } from "@/lib/nav-back"
import { useEffect, useRef, useState } from "react"

// The top-left of the signed-in chrome. Mirrors the bell top-right on every
// breakpoint, with the same visibility rules, so the strip reads as
// [action] — wordmark — bell.
//
// What it is depends on where you are:
//   - Off the dashboard: Dashboard (home for the active mode).
//   - On the dashboard: the one thing that mode reaches for most —
//     Barber → Rewards, Owner → Messages, Client → Explore shops.
//   - Already on that page: Dashboard again, so it is never a link to itself.
//
// BELOW A ROOT IT IS BACK INSTEAD. The shell has no navigation bar, so this was
// the only control in the corner where a back button belongs — and it was a
// shortcut: from a shop page it read "Dashboard" and threw you to the
// dashboard, with no way back to the listings you were browsing. See
// lib/nav-back for which screens count as roots and where Back goes when
// there is no history to pop (a push notification can open a shop page cold).
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/clients", "/preview"]

function shouldHide(rawPathname: string | null): boolean {
  if (!rawPathname) return false
  const pathname = stripLocalePrefix(rawPathname)
  if (isChromelessPath(pathname)) return true
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export function TopLeftAction() {
  const pathname = usePathname()
  const router = useRouter()
  const { userId, role, actualUserType, isLoading } = useUserRole()
  // How many times this web view has navigated since it loaded. router.back()
  // is only safe while this is above zero; before that the entry behind us
  // belongs to the shell's launch, not to anything the person did.
  const navigationsRef = useRef(0)
  const firstPathRef = useRef<string | null>(null)
  useEffect(() => {
    if (firstPathRef.current === null) firstPathRef.current = pathname ?? ""
    else if (pathname !== firstPathRef.current) navigationsRef.current += 1
  }, [pathname])
  const theme = useNavTheme("light")
  const [clientView, setClientView] = useState(false)
  useEffect(() => {
    setClientView(readClientView() || (pathname ?? "").startsWith("/dashboard/client"))
  }, [pathname])

  if (isLoading || !userId) return null
  if (shouldHide(pathname)) return null

  const effective = role ?? actualUserType
  const mode: "barber" | "owner" | "client" = clientView ? "client" : effective === "owner" ? "owner" : "barber"
  const onDashboard = (pathname ?? "").startsWith("/dashboard")

  const home = mode === "client" ? "/dashboard/client" : mode === "owner" ? "/dashboard/owner" : "/dashboard/barber"
  const reach =
    mode === "client"
      ? { label: "Explore", href: "/listings" }
      : mode === "owner"
        ? { label: "Messages", href: "/dashboard/owner/messages" }
        : { label: "Rewards", href: "/dashboard/barber/rewards" }

  const action = onDashboard && pathname !== reach.href ? reach : { label: "Dashboard", href: home }

  const dark = theme === "dark"

  const showBack = !isRootPath(pathname)
  const fallback = parentPath(pathname)

  const pill = `inline-flex h-7 md:h-9 items-center rounded-full text-[11px] md:text-xs font-medium shadow-[0_1px_2px_rgba(0,0,0,0.04)] md:shadow-sm transition-colors duration-200 ${
    dark
      ? "bg-[#0A0A0A] text-white border border-white/15 hover:border-white/30"
      : "bg-white dark:bg-card text-foreground border border-border hover:border-foreground/20"
  }`

  if (showBack && fallback) {
    return (
      <div className="fixed safe-top-action left-2 md:left-3 lg:left-[1.125rem] z-40">
        <button
          type="button"
          onClick={() => {
            // Pop only what this session actually pushed. Going "back" out of
            // the first page the app opened would leave the web view on
            // whatever preceded it, which is the sign-in route the shell
            // launches on.
            if (navigationsRef.current > 0) router.back()
            else router.push(fallback)
          }}
          aria-label="Back"
          className={`${pill} gap-0.5 pl-1.5 pr-2.5 md:pl-3 md:pr-4`}
        >
          <ChevronLeft className="h-3.5 w-3.5 md:h-4 md:w-4" aria-hidden />
          Back
        </button>
      </div>
    )
  }

  // Same pill as the dashboard's Request button: white with a hairline in
  // light mode, the card black in dark, text only. Over a dark page the pill
  // goes near-solid black so it still reads as a control.
  //
  // On the phone it is a compact 28px pill (h-7 at top-2.5) with tight
  // padding, so it reads as a quiet control next to the bell rather than a
  // slab: its centre sits on the same 24px line as the bell (36px at
  // top-1.5) and the wordmark. The Request-button size is desktop-only.
  //
  // The shadow is the dashboard's whisper (1px, barely there) instead of
  // Tailwind's shadow-sm — the softer, wider blur read as a halo around the
  // hairline at this size.
  return (
    <div className="fixed safe-top-action left-2 md:left-3 lg:left-[1.125rem] z-40">
      <Link
        href={action.href}
        className={`${pill} px-2.5 md:px-5`}
      >
        {action.label}
      </Link>
    </div>
  )
}
